import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import BasicModal from '../../../../components/common/BasicModal';
import inputSignalRulesService from 'services/inputSignalRulesService';
import { useNotificationContext } from 'contexts/NotificationContext';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';

const alertLabel = (count) => (count <= 1 ? 'alert' : 'alerts');
const alertVerb = (count) => (count <= 1 ? 'was' : 'were');

const RunAlertModal = ({ open, onClose, rule }) => {
	const { showNotification } = useNotificationContext();
	const [status, setStatus] = useState('default');
	const [result, setResult] = useState(null);
	const { tenant } = useIRWorkstation();
	const { trackButtonClick } = useTrackEvent({ tenant, product: 'IEM' });

	// reset state every time the modal is opened for a new alert
	useEffect(() => {
		if (open) {
			setStatus('default');
			setResult(null);
		}
	}, [open]);

	const handleButtonClickTrackEvent = (targetKey) => {
		const targetMap = {
			runRuleAction: ButtonClickTargets.AlertCenterRunRuleAction,
			runRuleCancel: ButtonClickTargets.AlertCenterRunRuleCancel,
			runRuleTest: ButtonClickTargets.AlertCenterRunRuleTest,
			runRuleNow: ButtonClickTargets.AlertCenterRunRuleNow,
			runRuleDone: ButtonClickTargets.AlertCenterRunRuleDone,
		};

		const target = targetMap[targetKey];
		if (target) {
			trackButtonClick(target);
		}
	};

	const handleClose = (type) => {
		switch(type) {
			case 'cancel':
				handleButtonClickTrackEvent('runRuleCancel')
				break;
      case 'done':
        handleButtonClickTrackEvent('runRuleDone')
        break;
      default:
				// type is 'close'
        handleButtonClickTrackEvent('runRuleCancel')
        break;
    }
		onClose();
	};

	// When user clicks "Test Alert", the modal remains open, and a notification is shown to display the result
	const handleTestAlert = async () => {
		handleButtonClickTrackEvent('runRuleTest');
		setStatus('testing');

		try {
			const { alerts_count } = await inputSignalRulesService.runInputSignalRule(
				{
					ruleId: rule.id,
					dryRun: true,
				}
			);

			const alertsCount = alerts_count ?? 0;
			showNotification(
				`Testing "${rule?.title}" — ${alertsCount} ${alertLabel(alertsCount)} would be created on the next run.`,
				'success'
			);
		} catch (err) {
			console.error(err.message);
			showNotification(`${err.message}`, 'error');
		} finally {
			setStatus('default');
		}
	};

	// When user clicks "Run Now", the modal shows a loading state while the alert is being run "Running alert now…"
	// Once complete, the modal shows a success or failure message
	const handleRunNow = async () => {
		handleButtonClickTrackEvent('runRuleNow');
		setStatus('processing');

		try {
			const { alerts_count } = await inputSignalRulesService.runInputSignalRule(
				{
					ruleId: rule.id,
					dryRun: false,
				}
			);
			setResult({ alertsCount: alerts_count ?? 0 });
			setStatus('success');
		} catch (err) {
			setResult({ error: err.message });
			setStatus('failure');
		}
	};

	const isLoading = status === 'testing' || status === 'processing';
	const alertsCount = result?.alertsCount ?? 0;

	const doneButton = (
		<Button variant='contained' onClick={() => handleClose('done')}>
			Done
		</Button>
	);

	const statusConfig = {
		default: {
			title: 'Run this alert now?',
			message: `This will re-run '${rule?.title}' right now against current data found within the linked report. If any records currently meet the condition, new alerts will be created for your user only. This does not affect the alert's regular schedule.`,
			actions: (
				<>
					<Button variant='outlined' onClick={() => handleClose('cancel')}>
						Cancel
					</Button>
					<LoadingButton
						variant='outlined'
						onClick={handleTestAlert}
						loading={status === 'testing'}
						disabled={isLoading}
					>
						Test Alert
					</LoadingButton>
					<Button
						variant='contained'
						onClick={handleRunNow}
						disabled={isLoading}
					>
						Run Now
					</Button>
				</>
			),
		},
		processing: {
			content: (
				<Stack
					direction='column'
					spacing={2}
					alignItems='center'
					justifyContent='center'
					sx={{ py: 4 }}
				>
					<CircularProgress size={24} />
					<Typography>Running alert now…</Typography>
				</Stack>
			),
		},
		success: {
			title: 'Alert ran successfully',
			message: `'${rule?.title}' ran successfully against the linked report. ${alertsCount} ${alertLabel(alertsCount)} ${alertVerb(alertsCount)} created for your user.`,
			actions: doneButton,
		},
		failure: {
			title: 'Alert could not be run',
			message: result?.error,
			actions: doneButton,
		},
	};

	// 'testing' reuses the default view — same title/message/actions, buttons disabled via isLoading
	const config = statusConfig[status] ?? statusConfig.default;

	// When the status is 'processing', show the processing content "Running alert now…"
	return (
		<BasicModal
			open={open}
			onClose={() => handleClose('close')}
			title={config.title}
			actions={config.actions}
		>
			{config.content ?? <Typography>{config.message}</Typography>}
		</BasicModal>
	);
};

RunAlertModal.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	rule: PropTypes.object,
};

export default RunAlertModal;
